/**
 * fuelClientCache — Phase 1 + Phase 2 (Audit Doc 11.06.2026)
 * ----------------------------------------------------------
 * Tiny client-side cache + fetch wrapper for the fuel system.
 *
 * Phase 1 (shipped earlier):
 *   • localStorage cache, 6h TTL (fuel sources publish ~once/day).
 *   • AbortController-based 12s timeout per request.
 *   • Retries on transient failures.
 *   • Stale-cache fallback on final failure.
 *
 * Phase 2 (this iteration — Structural):
 *   • Stale-while-revalidate — fresh-cache reads kick off a background
 *     refresh so the *next* visit gets newer data.
 *   • Exponential backoff with jitter (replaces linear).
 *   • Per-session retry budget (sessionStorage) — caps total retries to
 *     prevent runaway loops when the upstream is persistently failing.
 *
 * Surface area is intentionally small:
 *   fetchFuelWithCache(cacheKey, url, opts?)
 *     → { data, fromCache, stale, error, fetchedAt }
 *
 *   purgeFuelCache(prefix?)  — wipe matching entries (debug / manual refresh).
 *   resetFuelRetryBudget()   — admin/test helper.
 *
 * NOTE: only use this for read-only GETs on stable fuel endpoints
 * (`/api/fuel/prices/live`, `/api/fuel/stations/live`). Never wrap
 * mutating endpoints (alerts, favorites, submissions).
 */

const CACHE_PREFIX = 'pricepal_fuel_cache_v1::';
const DEFAULT_TTL_MS = 6 * 60 * 60 * 1000;      // 6 hours
const DEFAULT_TIMEOUT_MS = 12 * 1000;           // 12 seconds
const DEFAULT_RETRIES = 2;                       // 2 retries (3 attempts)

// Phase 2: exponential backoff with jitter
const BASE_BACKOFF_MS = 1000;                    // first retry waits ~1s
const MAX_BACKOFF_MS = 8 * 1000;                 // cap at 8s
const JITTER_MS = 300;                           // ±300ms random jitter

// Phase 2: per-session retry budget — kills runaway loops on persistent failure
const RETRY_BUDGET_KEY = 'pricepal_fuel_retry_budget_v1';
const DEFAULT_SESSION_RETRY_BUDGET = 12;         // total retries per browser session

// Phase 2: track in-flight background revalidations so we never double-fire
// for the same cache key.
const _inFlightRevalidations = new Set();

// Phase 3: error categorisation — distinct kinds drive distinct UI copy.
export const FuelErrorKind = Object.freeze({
  TIMEOUT: 'timeout',
  NETWORK: 'network',
  SERVER: 'server',     // 5xx / 429 after retries
  CLIENT: 'client',     // 4xx terminal
  UNKNOWN: 'unknown',
});

const _categoriseError = (err) => {
  if (!err) return FuelErrorKind.UNKNOWN;
  // AbortError → timeout (our AbortController fires after timeoutMs)
  if (err.name === 'AbortError') return FuelErrorKind.TIMEOUT;
  const msg = (err.message || String(err)).toLowerCase();
  if (msg.startsWith('http ')) {
    const status = parseInt(msg.replace('http', '').trim(), 10);
    if (status >= 500 || status === 429) return FuelErrorKind.SERVER;
    if (status >= 400) return FuelErrorKind.CLIENT;
  }
  if (msg.includes('failed to fetch') || msg.includes('networkerror') ||
      msg.includes('load failed') || msg.includes('net::')) {
    return FuelErrorKind.NETWORK;
  }
  return FuelErrorKind.UNKNOWN;
};

// Phase 3: human-readable copy per error kind. Pure function — easy to test.
export const fuelErrorCopy = (kind) => {
  switch (kind) {
    case FuelErrorKind.TIMEOUT:
      return 'Fuel data is taking longer than usual to load. We\u2019ll keep trying.';
    case FuelErrorKind.NETWORK:
      return 'You appear to be offline. Showing the last prices we saved.';
    case FuelErrorKind.SERVER:
      return 'Our fuel provider is having a hiccup. We\u2019re retrying in the background.';
    case FuelErrorKind.CLIENT:
      return 'Couldn\u2019t load fuel prices for this view. Try a different fuel type or location.';
    default:
      return 'Something went wrong loading fuel prices. Tap retry to try again.';
  }
};

const _safeStorage = () => {
  try {
    if (typeof window === 'undefined') return null;
    return window.localStorage || null;
  } catch {
    return null;
  }
};

const _safeSession = () => {
  try {
    if (typeof window === 'undefined') return null;
    return window.sessionStorage || null;
  } catch {
    return null;
  }
};

const readCache = (cacheKey) => {
  const store = _safeStorage();
  if (!store) return null;
  try {
    const raw = store.getItem(CACHE_PREFIX + cacheKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.timestamp !== 'number') return null;
    return parsed; // { data, timestamp }
  } catch {
    return null;
  }
};

const writeCache = (cacheKey, data, etag = null) => {
  const store = _safeStorage();
  if (!store) return;
  try {
    store.setItem(
      CACHE_PREFIX + cacheKey,
      JSON.stringify({ data, timestamp: Date.now(), etag })
    );
  } catch {
    // localStorage may be full or disabled — silently skip caching.
  }
};

const removeCache = (cacheKey) => {
  const store = _safeStorage();
  if (!store) return;
  try {
    store.removeItem(CACHE_PREFIX + cacheKey);
  } catch {
    // ignore
  }
};

// ─── Phase 2: retry-budget helpers ────────────────────────────────────────

const _getSessionBudget = () => {
  const store = _safeSession();
  if (!store) return Infinity; // no sessionStorage → no budget enforcement
  try {
    const raw = store.getItem(RETRY_BUDGET_KEY);
    if (raw === null) return DEFAULT_SESSION_RETRY_BUDGET;
    const n = parseInt(raw, 10);
    return Number.isNaN(n) ? DEFAULT_SESSION_RETRY_BUDGET : n;
  } catch {
    return DEFAULT_SESSION_RETRY_BUDGET;
  }
};

const _decrementSessionBudget = () => {
  const store = _safeSession();
  if (!store) return;
  try {
    const current = _getSessionBudget();
    if (current === Infinity) return;
    store.setItem(RETRY_BUDGET_KEY, String(Math.max(0, current - 1)));
  } catch {
    // ignore
  }
};

export const resetFuelRetryBudget = () => {
  const store = _safeSession();
  if (!store) return;
  try {
    store.setItem(RETRY_BUDGET_KEY, String(DEFAULT_SESSION_RETRY_BUDGET));
  } catch {
    // ignore
  }
};

// ─── Phase 2: exponential backoff with jitter ─────────────────────────────

const _computeBackoff = (attempt) => {
  // attempt is 0-indexed: attempt 0 → BASE, attempt 1 → BASE*2, ...
  const exp = BASE_BACKOFF_MS * 2 ** attempt;
  const capped = Math.min(exp, MAX_BACKOFF_MS);
  const jitter = Math.floor(Math.random() * JITTER_MS);
  return capped + jitter;
};

/**
 * Fetch wrapper with AbortController timeout, exponential backoff + jitter,
 * and a per-session retry budget.
 *
 * Phase 3: supports If-None-Match (ETag) for cheap revalidation. When the
 * server returns 304 Not Modified, we surface `{ notModified: true }` so the
 * caller can preserve the existing cached payload without re-parsing.
 *
 * Throws on final failure.
 */
const _fetchWithTimeoutAndRetry = async (
  url,
  {
    fetchOptions = {},
    timeoutMs = DEFAULT_TIMEOUT_MS,
    retries = DEFAULT_RETRIES,
    ifNoneMatch = null,
  } = {}
) => {
  let lastError = null;

  for (let attempt = 0; attempt <= retries; attempt += 1) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const headers = { ...(fetchOptions.headers || {}) };
      if (ifNoneMatch) headers['If-None-Match'] = ifNoneMatch;

      const response = await fetch(url, {
        ...fetchOptions,
        headers,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      // Phase 3: 304 Not Modified — cache is still fresh on the wire.
      if (response.status === 304) {
        return { json: null, etag: ifNoneMatch, notModified: true };
      }

      if (!response.ok) {
        // Treat 5xx + 429 as retryable; 4xx as terminal.
        if (response.status >= 500 || response.status === 429) {
          throw new Error(`HTTP ${response.status}`);
        }
        const terminal = new Error(`HTTP ${response.status}`);
        terminal.terminal = true;
        throw terminal;
      }

      const json = await response.json();
      const etag = response.headers.get('ETag') || null;
      return { json, etag, notModified: false };
    } catch (err) {
      clearTimeout(timeoutId);
      lastError = err;

      if (err && err.terminal) break;
      if (attempt < retries) {
        // Phase 2: respect the session-wide retry budget.
        if (_getSessionBudget() <= 0) break;
        _decrementSessionBudget();
        await new Promise((resolve) => setTimeout(resolve, _computeBackoff(attempt)));
      }
    }
  }

  throw lastError || new Error('Fuel request failed');
};

/**
 * Phase 2: silent background revalidation. Best-effort — never throws,
 * never updates UI state. Only refreshes the cache so the NEXT visit
 * gets newer data.
 *
 * Phase 3: passes If-None-Match and short-circuits on 304.
 */
const _revalidateInBackground = (cacheKey, url, opts, cachedEtag) => {
  if (_inFlightRevalidations.has(cacheKey)) return;
  if (_getSessionBudget() <= 0) return;
  _inFlightRevalidations.add(cacheKey);

  // Defer to next tick so the foreground return is fully synchronous.
  Promise.resolve().then(async () => {
    try {
      const res = await _fetchWithTimeoutAndRetry(url, {
        ...opts,
        ifNoneMatch: cachedEtag,
      });
      if (res.notModified) {
        // Nothing changed upstream — just bump the cache timestamp so the
        // 6h TTL doesn't lapse while the data is still good.
        const existing = readCache(cacheKey);
        if (existing) writeCache(cacheKey, existing.data, existing.etag || null);
      } else {
        // Phase 3+ enhancement: emit a price-delta event so the UI can
        // surface a small "prices updated" toast. The price-delta helper
        // is lazy-imported to keep this module dependency-free for tests.
        const previous = readCache(cacheKey);
        writeCache(cacheKey, res.json, res.etag);
        if (previous?.data && typeof window !== 'undefined' &&
            typeof window.dispatchEvent === 'function') {
          try {
            const { computePriceDeltas } = await import('./priceDelta');
            const deltas = computePriceDeltas(previous.data, res.json);
            if (deltas && deltas.items?.length > 0) {
              window.dispatchEvent(new CustomEvent('fuel-price-delta', {
                detail: { cacheKey, deltas, fetchedAt: Date.now() },
              }));
            }
          } catch {
            // delta computation is best-effort — never break the cache flow
          }
        }
      }
    } catch {
      // background failures are silent — UI keeps showing cached data
    } finally {
      _inFlightRevalidations.delete(cacheKey);
    }
  });
};

/**
 * Public API.
 * @param {string} cacheKey   Stable identifier (must include query/location).
 * @param {string} url        Full URL to fetch.
 * @param {object} opts
 * @param {boolean} opts.forceRefresh         Bypass cache, hit network.
 * @param {boolean} opts.staleWhileRevalidate Phase 2: when serving fresh
 *                                            cache, also kick off a silent
 *                                            background refresh. Default true.
 * @param {number}  opts.ttlMs                Override TTL (ms).
 * @param {number}  opts.timeoutMs            Per-request timeout (ms).
 * @param {number}  opts.retries              Number of retries.
 * @param {object}  opts.fetchOptions         Forwarded to fetch().
 * @returns {Promise<{
 *   data: any, fromCache: boolean, stale: boolean,
 *   error: Error|null, errorKind: string|null, fetchedAt: number
 * }>}
 */
export const fetchFuelWithCache = async (
  cacheKey,
  url,
  {
    forceRefresh = false,
    staleWhileRevalidate = true,
    ttlMs = DEFAULT_TTL_MS,
    timeoutMs = DEFAULT_TIMEOUT_MS,
    retries = DEFAULT_RETRIES,
    fetchOptions = {},
  } = {}
) => {
  const cached = readCache(cacheKey);
  const cachedEtag = cached?.etag || null;
  const now = Date.now();
  const cacheFresh =
    cached &&
    typeof cached.timestamp === 'number' &&
    now - cached.timestamp < ttlMs;

  if (cacheFresh && !forceRefresh) {
    // Phase 2: kick off silent background refresh so the next visit is fresher.
    if (staleWhileRevalidate) {
      _revalidateInBackground(
        cacheKey,
        url,
        { fetchOptions, timeoutMs, retries },
        cachedEtag
      );
    }
    return {
      data: cached.data,
      fromCache: true,
      stale: false,
      error: null,
      errorKind: null,
      fetchedAt: cached.timestamp,
    };
  }

  try {
    // Phase 3: include If-None-Match on foreground revalidation too.
    const res = await _fetchWithTimeoutAndRetry(url, {
      fetchOptions,
      timeoutMs,
      retries,
      ifNoneMatch: forceRefresh ? null : cachedEtag,
    });
    if (res.notModified && cached) {
      // Server confirmed our cache is still fresh — keep payload, bump TS.
      writeCache(cacheKey, cached.data, cached.etag || null);
      return {
        data: cached.data,
        fromCache: true,
        stale: false,
        error: null,
        errorKind: null,
        fetchedAt: Date.now(),
      };
    }
    writeCache(cacheKey, res.json, res.etag);
    return {
      data: res.json,
      fromCache: false,
      stale: false,
      error: null,
      errorKind: null,
      fetchedAt: Date.now(),
    };
  } catch (err) {
    const errorKind = _categoriseError(err);
    // Fall back to stale cache if available — graceful degradation.
    if (cached) {
      return {
        data: cached.data,
        fromCache: true,
        stale: true,
        error: err,
        errorKind,
        fetchedAt: cached.timestamp,
      };
    }
    return {
      data: null,
      fromCache: false,
      stale: false,
      error: err,
      errorKind,
      fetchedAt: 0,
    };
  }
};

/**
 * Wipe cache entries. Pass a prefix to scope (e.g. 'stations:'),
 * or no argument to clear everything in the fuel cache namespace.
 */
export const purgeFuelCache = (prefix = '') => {
  const store = _safeStorage();
  if (!store) return;
  try {
    const fullPrefix = CACHE_PREFIX + prefix;
    const keysToRemove = [];
    for (let i = 0; i < store.length; i += 1) {
      const k = store.key(i);
      if (k && k.startsWith(fullPrefix)) keysToRemove.push(k);
    }
    keysToRemove.forEach((k) => store.removeItem(k));
  } catch {
    // ignore
  }
};

// Exposed for tests + manual debugging.
export const _internals = {
  CACHE_PREFIX,
  DEFAULT_TTL_MS,
  DEFAULT_TIMEOUT_MS,
  DEFAULT_RETRIES,
  BASE_BACKOFF_MS,
  MAX_BACKOFF_MS,
  JITTER_MS,
  DEFAULT_SESSION_RETRY_BUDGET,
  RETRY_BUDGET_KEY,
  readCache,
  writeCache,
  removeCache,
  _computeBackoff,
  _getSessionBudget,
  _categoriseError,
};
